import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ImageUploader from './ImageUploader';

const GalleryManagement = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    category: '',
    tags: ''
  });
  const [imagePreview, setImagePreview] = useState({ url: '', status: 'idle', error: '' }); // idle, loading, success, error

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
      } else {
        setError('Failed to fetch gallery images');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Trigger image preview validation when URL changes
    if (name === 'image_url' && value) {
      validateImageUrl(value);
    } else if (name === 'image_url' && !value) {
      setImagePreview({ url: '', status: 'idle', error: '' });
    }
  };

  const validateImageUrl = (url) => {
    setImagePreview({ url: '', status: 'loading', error: '' });
    
    const img = new Image();
    img.onload = () => {
      setImagePreview({ url: url, status: 'success', error: '' });
    };
    img.onerror = () => {
      setImagePreview({ url: '', status: 'error', error: 'Failed to load image. Please check the URL.' });
    };
    img.src = url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const submitData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        setFormData({ title: '', image_url: '', category: '', tags: '' });
        setShowAddForm(false);
        fetchImages();
      } else {
        alert('Failed to add gallery image');
      }
    } catch (err) {
      alert('Connection error');
    }
  };

  const handleFileUploadSuccess = async (uploadResults) => {
    // For each uploaded file, add it to the gallery
    for (const result of uploadResults) {
      if (result.success && result.url) {
        try {
          const token = localStorage.getItem('adminToken');
          const galleryData = {
            title: result.original_name || result.filename,
            image_url: `${process.env.REACT_APP_BACKEND_URL}${result.url}`,
            category: 'Uploads',
            tags: ['uploaded']
          };

          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(galleryData)
          });
        } catch (err) {
          console.error('Failed to add to gallery:', err);
        }
      }
    }
    
    // Refresh gallery
    fetchImages();
    setShowAddForm(false);
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchImages();
      } else {
        alert('Failed to delete image');
      }
    } catch (err) {
      alert('Connection error');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gallery Management</h1>
          <p className="text-gray-600 mt-2">Manage gallery images and content</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {showAddForm ? 'Cancel' : 'Add New Image'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Add Image Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Gallery Image</h3>
          
          {/* Upload Mode Toggle */}
          <div className="flex space-x-2 mb-6">
            <button
              type="button"
              onClick={() => setUploadMode('file')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                uploadMode === 'file' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📤 Upload from Device
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('url')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                uploadMode === 'url' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🔗 Add from URL
            </button>
          </div>

          {uploadMode === 'file' ? (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Select images from your computer to upload directly to the gallery.
              </p>
              <ImageUploader 
                onUploadSuccess={handleFileUploadSuccess}
                folder="gallery"
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Marine Life, Diving, Equipment"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
                
                {/* Image Preview Section */}
                {imagePreview.status === 'loading' && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center text-gray-600">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                      Validating image URL...
                    </div>
                  </div>
                )}
                
                {imagePreview.status === 'success' && (
                  <div className="mt-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center text-green-700 mb-2">
                      <span className="text-lg mr-2">✓</span>
                      Image URL is valid
                    </div>
                    <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={imagePreview.url} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                
                {imagePreview.status === 'error' && (
                  <div className="mt-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center text-red-700">
                      <span className="text-lg mr-2">⚠️</span>
                      {imagePreview.error}
                    </div>
                    <p className="text-sm text-red-600 mt-1">
                      The image may not exist, be blocked by CORS, or the URL may be incorrect.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="diving, underwater, marine, adventure"
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setImagePreview({ url: '', status: 'idle', error: '' });
                    setFormData({ title: '', image_url: '', category: '', tags: '' });
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={imagePreview.status === 'loading' || imagePreview.status === 'error'}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {imagePreview.status === 'loading' ? 'Validating...' : 'Add Image'}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      )}

      {/* Gallery Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {images.map((image, index) => (
          <motion.div
            key={image.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="aspect-w-16 aspect-h-12">
              <img
                src={image.image_url}
                alt={image.title}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                }}
              />
            </div>
            
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-1">{image.title}</h3>
              <p className="text-sm text-gray-600 mb-2">{image.category}</p>
              
              {image.tags && image.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {image.tags.slice(0, 3).map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {image.tags.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      +{image.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  {image.created_at ? new Date(image.created_at).toLocaleDateString() : 'N/A'}
                </span>
                <button
                  onClick={() => deleteImage(image.id)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {images.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-500 mb-4">No gallery images found.</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Your First Image
          </button>
        </div>
      )}
    </div>
  );
};

export default GalleryManagement;
